import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NettbutikkOrdrerTab from "../components/nettbutikk/NettbutikkOrdrerTab";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

const BUTIKK_LABEL = {
  royken: "Sport 1 Røyken",
  slemmestad: "Sport 1 Slemmestad",
};

const STATUS_LABEL = { betalt: "Betalt", refundert: "Refundert" };

const BETALINGSMETODE_LABEL = { vipps: "Vipps", klarna: "Klarna" };
const BETALINGSMETODE_BADGE = {
  vipps: "bg-orange-100 text-orange-800",
  klarna: "bg-pink-100 text-pink-800",
};

function isoDate(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function fmtKr(n) {
  return `${Math.round(Number(n) || 0).toLocaleString("nb-NO")} kr`;
}

function fmtDate(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";
}

function csvNum(n) {
  return round2(n).toString().replace(".", ",");
}

function downloadCsv(filename, headers, dataRows) {
  const escape = (v) => {
    const s = String(v ?? "");
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers, ...dataRows].map((row) => row.map(escape).join(";"));
  const csv = "﻿" + lines.join("\r\n"); // BOM slik at Excel leser æøå riktig
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Kvittering for én ordre - all data finnes allerede i raden fra
// /nettbutikk-ordre/salgsrapport (bygget av buildNettbutikkPaymentRow), så
// ingen nytt API-kall trengs. Et bilagsnummer finnes kun for
// production-betalinger (se buildNettbutikkSalgsbilag) - test-ordrer vises
// tydelig merket som det, ikke som et offisielt salgsbilag.
function Kvittering({ row, onLukk }) {
  const erTest = row.miljo === "test" || !row.kvitteringsnummer;

  return (
    <div className="bg-white rounded-lg border p-5 print:border-0 print:shadow-none">
      <div className="flex justify-between items-center mb-4 print:hidden">
        <h3 className="text-lg font-semibold">Kvittering {row.referanse}</h3>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50">
            Skriv ut
          </button>
          <button onClick={onLukk} className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50">
            Lukk
          </button>
        </div>
      </div>

      {erTest && (
        <div className="rounded border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2 mb-4 print:border-black print:bg-white">
          Dette er en testkvittering (testmiljø) - ikke et offisielt salgsbilag.
        </div>
      )}

      <div className="flex justify-between mb-4">
        <div>
          <p className="font-semibold">
            {row.kvitteringsnummer ? `Salgsbilag ${row.kvitteringsnummer}` : "Testkvittering"}
          </p>
          <p className="text-gray-500 text-sm">{fmtDate(row.betaltAt)}</p>
          <p className="text-gray-500 text-sm">Ordre: {row.referanse}</p>
        </div>
        <div className="text-right text-sm text-gray-600">
          {row.selger?.navn && <p className="font-medium text-gray-800">{row.selger.navn}</p>}
          {row.selger?.orgnr && <p>Org.nr. {row.selger.orgnr}</p>}
          <p>{BUTIKK_LABEL[row.butikk] || row.butikk}</p>
          <p>
            Betalt med{" "}
            <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${BETALINGSMETODE_BADGE[row.betalingsmetode] || "bg-gray-100 text-gray-700"}`}>
              {BETALINGSMETODE_LABEL[row.betalingsmetode] || row.betalingsmetode}
            </span>
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mb-4 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-2 py-1.5 text-left">Vare</th>
              <th className="px-2 py-1.5 text-left">Størrelse</th>
              <th className="px-2 py-1.5 text-right">Antall</th>
              <th className="px-2 py-1.5 text-right">Pris pr.</th>
              <th className="px-2 py-1.5 text-right">Sum</th>
            </tr>
          </thead>
          <tbody>
            {(row.linjer || []).map((l, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-2 py-1.5">{l.navn || l.ean || "Linje"}</td>
                <td className="px-2 py-1.5">{l.storrelse || "—"}</td>
                <td className="px-2 py-1.5 text-right">{l.antall}</td>
                <td className="px-2 py-1.5 text-right">{fmtKr(l.prisPerStk)}</td>
                <td className="px-2 py-1.5 text-right">{fmtKr((Number(l.prisPerStk) || 0) * (Number(l.antall) || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-right text-sm space-y-0.5">
        <p>Eks. mva: {fmtKr(row.sumEksMva)}</p>
        <p>Mva ({row.mvaSats ?? "—"} %): {fmtKr(row.mvaBelop)}</p>
        <p className="font-semibold text-base">Totalt: {fmtKr(row.totalInkMva)}</p>
        {row.refundertBelop > 0 && (
          <p className="text-orange-700">Refundert: {fmtKr(row.refundertBelop)}</p>
        )}
      </div>
    </div>
  );
}

export default function NettbutikkSalgsrapport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [butikk, setButikk] = useState("alle");
  const [view, setView] = useState("ordrer"); // ordrer | oversikt | journal
  const [valgtRad, setValgtRad] = useState(null);

  const today = useMemo(() => new Date(), []);
  const monthDate = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset]
  );
  const year = monthDate.getFullYear();
  const monthIndex0 = monthDate.getMonth();
  const isCurrentMonth = monthOffset === 0;

  const firstDayISO = useMemo(() => isoDate(new Date(year, monthIndex0, 1)), [year, monthIndex0]);
  const lastDayISO = useMemo(
    () => isoDate(new Date(year, monthIndex0, daysInMonth(year, monthIndex0))),
    [year, monthIndex0]
  );

  // Åpnet salgsrapport = nye ordre er sett. NavBar lytter på eventet og
  // oppdaterer varselet på Nettbutikk-fanen.
  useEffect(() => {
    fetch(`${API_BASE_URL}/nettbutikk-ordre/marker-sett`, { method: "POST" })
      .then((r) => { if (r.ok) window.dispatchEvent(new Event("nettbutikkOrdreSett")); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setValgtRad(null);
      try {
        const r = await fetch(
          `${API_BASE_URL}/nettbutikk-ordre/salgsrapport?butikk=${encodeURIComponent(butikk)}&from=${firstDayISO}&to=${lastDayISO}`
        );
        const data = r.ok ? await r.json() : { rows: [] };
        setRows(data.rows || []);
      } catch (err) {
        console.error("Feil ved henting av nettbutikk-salgsrapport:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [firstDayISO, lastDayISO, butikk]);

  const summary = useMemo(() => {
    let totaltSalg = 0, sumEksMva = 0, mvaBelop = 0, refundert = 0, testmiljoAntall = 0;
    const perButikk = {};
    const perBetalingsmetode = {};

    for (const r of rows) {
      totaltSalg += Number(r.totalInkMva) || 0;
      sumEksMva += Number(r.sumEksMva) || 0;
      mvaBelop += Number(r.mvaBelop) || 0;
      refundert += Number(r.refundertBelop) || 0;
      if (r.miljo === "test") testmiljoAntall += 1;

      const bKey = r.butikk || "ukjent";
      if (!perButikk[bKey]) perButikk[bKey] = { antall: 0, totalt: 0 };
      perButikk[bKey].antall += 1;
      perButikk[bKey].totalt += Number(r.totalInkMva) || 0;

      const mKey = r.betalingsmetode || "vipps";
      if (!perBetalingsmetode[mKey]) perBetalingsmetode[mKey] = { antall: 0, totalt: 0 };
      perBetalingsmetode[mKey].antall += 1;
      perBetalingsmetode[mKey].totalt += Number(r.totalInkMva) || 0;
    }

    return {
      antall: rows.length,
      totaltSalg: round2(totaltSalg),
      nettoSalg: round2(sumEksMva),
      mvaBelop: round2(mvaBelop),
      refundert: round2(refundert),
      testmiljoAntall,
      perButikk,
      perBetalingsmetode,
    };
  }, [rows]);

  function exportSalgsjournal() {
    const headers = ["Bilag", "Dato", "Ordre", "Butikk", "Betalingsmåte", "Eks. mva", "Mva", "Totalt", "Status", "Miljø"];
    const data = rows.map((r) => [
      r.kvitteringsnummer || "",
      fmtDate(r.betaltAt),
      r.referanse,
      BUTIKK_LABEL[r.butikk] || r.butikk,
      BETALINGSMETODE_LABEL[r.betalingsmetode] || r.betalingsmetode || "",
      csvNum(r.sumEksMva),
      csvNum(r.mvaBelop),
      csvNum(r.totalInkMva),
      STATUS_LABEL[r.status] || r.status,
      r.miljo || "",
    ]);
    downloadCsv(`nettbutikk-salgsjournal-${year}-${String(monthIndex0 + 1).padStart(2, "0")}.csv`, headers, data);
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold">Salgsrapport nettbutikk</h2>
        <button onClick={() => navigate(-1)} className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300">
          Tilbake
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-3 mb-4">
        {[
          ["ordrer", "Ordrer og sending"],
          ["oversikt", "Oversikt"],
          ["journal", "Salgsjournal"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`px-4 py-2 rounded text-sm ${view === key ? "bg-blue-600 text-white" : "bg-gray-100 hover:bg-gray-200"}`}
          >
            {label}
          </button>
        ))}
        {view === "ordrer" && (
          <select
            aria-label="Butikk"
            value={butikk}
            onChange={(e) => setButikk(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm bg-white ml-auto"
          >
            <option value="alle">Alle butikker</option>
            <option value="royken">Sport 1 Røyken</option>
            <option value="slemmestad">Sport 1 Slemmestad</option>
          </select>
        )}
      </div>

      {view !== "ordrer" && (
        <>
      {summary.testmiljoAntall > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-2 mb-4">
          {summary.testmiljoAntall} av {summary.antall} ordre i denne perioden er fra Vipps sitt <strong>testmiljø</strong> (fiktive penger, ikke reelt salg).
        </div>
      )}

      <div className="rounded border bg-green-50 p-4 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-600">Salgsrapport</div>
            <div className="text-xl font-bold">{MONTH_NAMES[monthIndex0]} {year}</div>
            <div className="text-sm text-gray-700 mt-1">{firstDayISO} – {lastDayISO}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={butikk}
              onChange={(e) => setButikk(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="alle">Alle butikker</option>
              <option value="royken">Sport 1 Røyken</option>
              <option value="slemmestad">Sport 1 Slemmestad</option>
            </select>
            <button
              onClick={() => setMonthOffset((o) => o - 1)}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
            >
              ← Forrige
            </button>
            {!isCurrentMonth && (
              <button
                onClick={() => setMonthOffset(0)}
                className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-green-700"
              >
                Denne måneden
              </button>
            )}
            <button
              onClick={() => setMonthOffset((o) => Math.min(0, o + 1))}
              disabled={isCurrentMonth}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Neste →
            </button>
          </div>
        </div>
      </div>


        </>
      )}

      {view === "ordrer" ? (
        <NettbutikkOrdrerTab butikk={butikk} />
      ) : loading ? (
        <p className="text-gray-400 text-sm">Laster …</p>
      ) : view === "oversikt" ? (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="min-w-full max-w-md border rounded bg-white text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left"></th>
                  <th className="px-3 py-2 text-right">Beløp inkl. mva</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t font-semibold"><td className="px-3 py-2">Totalt salg</td><td className="px-3 py-2 text-right">{fmtKr(summary.totaltSalg)}</td></tr>
                <tr className="border-t"><td className="px-3 py-2">Netto salg (eks. mva)</td><td className="px-3 py-2 text-right">{fmtKr(summary.nettoSalg)}</td></tr>
                <tr className="border-t"><td className="px-3 py-2">Utgående mva 25 %</td><td className="px-3 py-2 text-right">{fmtKr(summary.mvaBelop)}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded border p-4 bg-white">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Antall ordre</div>
              <div className="text-2xl font-bold mt-1">{summary.antall}</div>
            </div>
            {summary.refundert > 0 && (
              <div className="rounded border p-4 bg-orange-50">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Refusjoner</div>
                <div className="text-2xl font-bold mt-1 text-orange-700">− {fmtKr(summary.refundert)}</div>
              </div>
            )}
            <div className="rounded border p-4 bg-blue-50 col-span-2 md:col-span-1">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Totalt salg etter refusjoner</div>
              <div className="text-2xl font-bold mt-1">{fmtKr(summary.totaltSalg - summary.refundert)}</div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Salg per butikk</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border rounded bg-white text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Butikk</th>
                    <th className="px-3 py-2 text-right">Antall</th>
                    <th className="px-3 py-2 text-right">Totalt</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.perButikk).map(([slug, v]) => (
                    <tr key={slug} className="border-t">
                      <td className="px-3 py-2">{BUTIKK_LABEL[slug] || slug}</td>
                      <td className="px-3 py-2 text-right">{v.antall}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtKr(v.totalt)}</td>
                    </tr>
                  ))}
                  {Object.keys(summary.perButikk).length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">Ingen salg denne måneden</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Salg per betalingsmåte</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border rounded bg-white text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Betalingsmåte</th>
                    <th className="px-3 py-2 text-right">Antall</th>
                    <th className="px-3 py-2 text-right">Totalt</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.perBetalingsmetode).map(([metode, v]) => (
                    <tr key={metode} className="border-t">
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${BETALINGSMETODE_BADGE[metode] || "bg-gray-100 text-gray-700"}`}>
                          {BETALINGSMETODE_LABEL[metode] || metode}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{v.antall}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtKr(v.totalt)}</td>
                    </tr>
                  ))}
                  {Object.keys(summary.perBetalingsmetode).length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">Ingen salg denne måneden</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : valgtRad ? (
        <Kvittering row={valgtRad} onLukk={() => setValgtRad(null)} />
      ) : (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={exportSalgsjournal} className="px-3 py-1.5 rounded border text-sm bg-white hover:bg-gray-50">
              Last ned salgsjournal (CSV)
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border rounded bg-white text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Bilag</th>
                  <th className="px-3 py-2 text-left">Dato</th>
                  <th className="px-3 py-2 text-left">Ordre</th>
                  <th className="px-3 py-2 text-left">Butikk</th>
                  <th className="px-3 py-2 text-left">Betalingsmåte</th>
                  <th className="px-3 py-2 text-right">Eks. mva</th>
                  <th className="px-3 py-2 text-right">Mva</th>
                  <th className="px-3 py-2 text-right">Totalt</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Miljø</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setValgtRad(r)}
                    className="border-t hover:bg-blue-50 cursor-pointer"
                    title="Vis kvittering"
                  >
                    <td className="px-3 py-2">{r.kvitteringsnummer || "—"}</td>
                    <td className="px-3 py-2">{fmtDate(r.betaltAt)}</td>
                    <td className="px-3 py-2">{r.referanse}</td>
                    <td className="px-3 py-2">{BUTIKK_LABEL[r.butikk] || r.butikk}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${BETALINGSMETODE_BADGE[r.betalingsmetode] || "bg-gray-100 text-gray-700"}`}>
                        {BETALINGSMETODE_LABEL[r.betalingsmetode] || r.betalingsmetode}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{fmtKr(r.sumEksMva)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(r.mvaBelop)}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtKr(r.totalInkMva)}</td>
                    <td className="px-3 py-2">{STATUS_LABEL[r.status] || r.status}</td>
                    <td className="px-3 py-2">
                      {r.miljo === "test" ? (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs">Test</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs">Produksjon</span>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-500">Ingen salg denne måneden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
